import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function HomePage() {
  const [stats, setStats] = useState(null);
  const [recentStories, setRecentStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, storiesRes] = await Promise.all([
        fetch('/api/stats'),
        fetch('/api/stories?limit=6&sort=updated_at')
      ]);
      
      const statsData = await statsRes.json();
      const storiesData = await storiesRes.json();
      
      if (statsData.stats) setStats(statsData.stats);
      if (storiesData.stories) setRecentStories(storiesData.stories);
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero 区域 */}
      <section className="bg-gradient-to-r from-purple-600 via-primary-600 to-blue-600 rounded-lg p-8 md:p-12">
        <div className="text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            📚 Novel Relay
          </h1>
          <p className="text-lg md:text-xl text-gray-200 mb-2">
            多人接力小说创作平台
          </p>
          <p className="text-sm md:text-base text-gray-300 mb-8 max-w-2xl mx-auto">
            一个人写开头，一群人续精彩。在这里，每个人都可以成为故事的创作者，
            与他人共同编织无限可能的剧情世界。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/stories"
              className="px-8 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              📖 探索故事
            </Link>
            <Link
              to="/create"
              className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
            >
              ✍️ 开创新故事
            </Link>
          </div>
        </div>
      </section>

      {/* 统计数据 */}
      {stats && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
            <div className="text-3xl font-bold text-primary-400">{stats.users}</div>
            <div className="text-gray-400 mt-1">👥 作者</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
            <div className="text-3xl font-bold text-green-400">{stats.stories}</div>
            <div className="text-gray-400 mt-1">📚 故事</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
            <div className="text-3xl font-bold text-blue-400">{stats.chapters}</div>
            <div className="text-gray-400 mt-1">📝 章节</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
            <div className="text-3xl font-bold text-red-400">{stats.activities}</div>
            <div className="text-gray-400 mt-1">💬 动态</div>
          </div>
        </section>
      )}

      {/* 最新故事 */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">🔥 最新更新</h2>
          <Link to="/stories" className="text-primary-400 hover:text-primary-300">
            查看全部 →
          </Link>
        </div>
        
        {loading ? (
          <div className="text-center py-8 text-gray-400">加载中...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentStories.map(story => (
              <Link
                key={story.id}
                to={`/stories/${story.id}`}
                className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-primary-500 transition-colors"
              >
                <h3 className="text-lg font-bold mb-2 truncate">{story.title}</h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-3">
                  {story.description}
                </p>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>📖 {story.views || 0}</span>
                  <span>❤️ {story.likes || 0}</span>
                  <span className="px-2 py-1 bg-gray-700 rounded">
                    {story.category || '其他'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* 功能特色 */}
      <section className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-2xl font-bold mb-6 text-center">✨ 平台特色</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-4xl mb-3">✍️</div>
            <h3 className="text-lg font-bold mb-2">便捷创作</h3>
            <p className="text-gray-400 text-sm">
              简洁的编辑器，让您专注于创作本身
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-3">📚</div>
            <h3 className="text-lg font-bold mb-2">章节管理</h3>
            <p className="text-gray-400 text-sm">
              轻松组织和管理您的故事章节
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-3">👥</div>
            <h3 className="text-lg font-bold mb-2">社区互动</h3>
            <p className="text-gray-400 text-sm">
              与读者互动，获取反馈和建议
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
